/**
 * Transforms flat AI beverage response to structuredBeverageData format
 * that the save-beverage-preferences action expects.
 */

// Mapping from AI category codes to human-readable category/type names
export const codeToCategory = {
  // Non-Alcoholic
  cofdecaf: { category: "Coffee", type: "Decaf" },
  coffee: { category: "Coffee", type: "Regular" },
  cofspclty: { category: "Coffee", type: "Specialty" },
  enrgydrk: { category: "Energy Drink", type: "Energy Drink" },
  juice: { category: "Juice", type: "Juice" },
  milk: { category: "Milk", type: "Regular" },
  nondairymlk: { category: "Milk", type: "Non Dairy" },
  smooth: { category: "Smoothie", type: "Smoothie" },
  sodacola: { category: "Soda", type: "Regular" },
  sodadiet: { category: "Soda", type: "Diet" },
  sodaggale: { category: "Soda", type: "Ginger Ale" },
  sodaothr: { category: "Soda", type: "Other" },
  tea: { category: "Tea", type: "Regular" },
  teadecaf: { category: "Tea", type: "Decaf" },
  water: { category: "Water", type: "Regular" },
  waterspec: { category: "Water", type: "Sparkling" },
  // Alcoholic
  beer: { category: "Beer", type: "Alcoholic" },
  borbn: { category: "Spirits", type: "Bourbon" },
  chmpsprk: { category: "Wine", type: "Sparkling" },
  cognbrdy: { category: "Spirits", type: "Cognac/Brandy" },
  gin: { category: "Spirits", type: "Gin" },
  nonalbeer: { category: "Beer", type: "Non Alcoholic" },
  nonalmcktl: { category: "Spirits", type: "Mocktail" },
  redwne: { category: "Wine", type: "Red" },
  rosewne: { category: "Wine", type: "Rose" },
  rum: { category: "Spirits", type: "Rum" },
  scotch: { category: "Spirits", type: "Scotch" },
  teqla: { category: "Spirits", type: "Tequila" },
  vodka: { category: "Spirits", type: "Vodka" },
  whsky: { category: "Spirits", type: "Whiskey" },
  whtwne: { category: "Wine", type: "White" },
};

/**
 * Capitalizes first letter of each word (Title Case)
 */
const toTitleCase = (str) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Parses description string in format "{drink} at {time}" or "{drink}"
 * Returns { drink, timeOfDay } where timeOfDay is an array of Title Case times
 */
const parseDescription = (description) => {
  if (!description || typeof description !== "string") {
    return { drink: "", timeOfDay: [] };
  }

  const parts = description.split(" at ");
  const drink = parts[0]?.trim() || "";

  if (parts.length < 2 || !parts[1]) {
    return { drink, timeOfDay: [] };
  }

  // Parse time(s) - could be "morning", "morning, evening", etc.
  const timeStr = parts[1].trim();
  const times = timeStr
    .split(",")
    .map((t) => toTitleCase(t.trim()))
    .filter(Boolean);

  return { drink, timeOfDay: times };
};

/**
 * Transforms flat AI beverage response to structuredBeverageData format
 *
 * @param {Object} aiBeverages - The flat AI response with beverageAlcoholicLov, beverageAlcoholicDesc, etc.
 * @returns {Array} structuredBeverageData array for the save action
 */
export const transformAiBeverageData = (aiBeverages) => {
  if (!aiBeverages) {
    return [];
  }

  const {
    beverageAlcoholicLov = {},
    beverageAlcoholicDesc = {},
    beverageNonAlcoholicLov = {},
    beverageNonAlcoholicDesc = {},
  } = aiBeverages;

  // Group by category
  const categoryMap = {};

  // Process alcoholic beverages
  Object.entries(beverageAlcoholicLov).forEach(([code, isSelected]) => {
    if (!isSelected) return;

    const mapping = codeToCategory[code];
    if (!mapping) return;

    const { category, type } = mapping;
    const description = beverageAlcoholicDesc[code] || "";
    const { drink, timeOfDay } = parseDescription(description);

    if (!categoryMap[category]) {
      categoryMap[category] = { category, types: [] };
    }

    categoryMap[category].types.push({
      type,
      additionalDetails: drink,
      timeOfDay,
    });
  });

  // Process non-alcoholic beverages
  Object.entries(beverageNonAlcoholicLov).forEach(([code, isSelected]) => {
    if (!isSelected) return;

    const mapping = codeToCategory[code];
    if (!mapping) return;

    const { category, type } = mapping;
    const description = beverageNonAlcoholicDesc[code] || "";
    const { drink, timeOfDay } = parseDescription(description);

    if (!categoryMap[category]) {
      categoryMap[category] = { category, types: [] };
    }

    categoryMap[category].types.push({
      type,
      additionalDetails: drink,
      timeOfDay,
    });
  });

  return Object.values(categoryMap);
};
